import {
  FormFieldArraySchema,
  FormFieldCustomSchema,
  FormFieldSchema,
  FormSchema,
} from 'app/shared/models/form';
import {
  FormField,
  FormFieldSection,
  FormFields,
} from 'app/modules/filingFormGenerator/models';
import { GoamlReferenceTables } from 'app/modules/goAML/queries/useGetGoamlFiuConfig';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

// ponytail: FormField<T> has contravariant validate positions; Record<string,unknown> breaks tsc
// when adaptLegacySchema output is assigned to FormFields<GoamlGeneratedFormValues>. any is load-bearing.
type LegacyField =
  | FormFieldSchema<any>
  | FormFieldCustomSchema<any>
  | FormFieldArraySchema<any>;

type ReferenceTableKey = keyof GoamlReferenceTables;

const FIELD_REFERENCE_TABLES: Record<string, ReferenceTableKey> = {
  account_type: 'account_type',
  address_type: 'contact_type',
  country_code: 'country_code',
  currency_code: 'currency_code',
  currency_code_local: 'currency_code',
  foreign_currency_code: 'currency_code',
  from_country: 'country_code',
  from_funds_code: 'funds_type',
  gender: 'gender',
  incorporation_country_code: 'country_code',
  incorporation_legal_form: 'legal_form',
  issue_country: 'country_code',
  issued_by: 'country_code',
  nationality1: 'country_code',
  nationality2: 'country_code',
  nationality3: 'country_code',
  passport_country: 'country_code',
  personal_account_type: 'account_type',
  report_code: 'report_code',
  residence: 'country_code',
  status_code: 'account_status',
  submission_code: 'submission_type',
  to_country: 'country_code',
  to_funds_code: 'funds_type',
  tph_communication_type: 'communication_type',
  tph_contact_type: 'contact_type',
  transmode_code: 'transmode_code',
  type: 'identifier_type',
};

const getReferenceTableKey = (
  field: FormFieldSchema<any>,
): ReferenceTableKey | undefined => {
  if (field.name === 'indicator') {
    return 'report_indicators';
  }

  if (field.name === 'role') {
    const values = new Set(
      (field.fieldProps?.options ?? []).map(({ value }) => value),
    );
    if (values.has('CEO')) {
      return 'account_person_role';
    }
    if (values.has('AU')) {
      return 'entity_person_role';
    }
  }

  return FIELD_REFERENCE_TABLES[field.name];
};

const withReferenceTableOptions = (
  field: FormFieldSchema<any>,
  referenceTables: GoamlReferenceTables,
): FormFieldSchema<any> => {
  const referenceTableKey = getReferenceTableKey(field);
  const options = referenceTableKey ? referenceTables[referenceTableKey] : null;

  if (!options || !field.fieldProps?.options) {
    return field;
  }

  return {
    ...field,
    fieldProps: {
      ...field.fieldProps,
      options,
    },
  };
};

const toGeneratedField = (
  field: FormFieldSchema<any>,
  referenceTables: GoamlReferenceTables,
): FormField<Record<string, any>> => {
  const adaptedField = withReferenceTableOptions(field, referenceTables);
  const baseField = {
    name: adaptedField.name,
    label: adaptedField.label ?? adaptedField.name,
    help: adaptedField.help,
    required: adaptedField.required,
    fieldProps: adaptedField.fieldProps,
    isHidden: adaptedField.hidden,
    defaultValue:
      typeof adaptedField.default === 'function'
        ? undefined
        : adaptedField.default,
    mustExistInPayload: Boolean(adaptedField.required),
    mustHaveValueIfExist: Boolean(adaptedField.required),
  };

  switch (adaptedField.type) {
    case U21FormFieldTypes.CHECKBOX:
      return {
        ...baseField,
        type: U21FormFieldTypes.CHECKBOX,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.DATE_PICKER:
      return {
        ...baseField,
        type: U21FormFieldTypes.DATE_PICKER,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.DATE_TIME_PICKER:
      return {
        ...baseField,
        type: U21FormFieldTypes.DATE_TIME_PICKER,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.MULTISELECT:
      return {
        ...baseField,
        type: U21FormFieldTypes.MULTISELECT,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.NUMBER:
      return {
        ...baseField,
        type: U21FormFieldTypes.NUMBER,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.RADIO_GROUP:
      return {
        ...baseField,
        type: U21FormFieldTypes.RADIO_GROUP,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.SELECT:
      return {
        ...baseField,
        type: U21FormFieldTypes.SELECT,
      } satisfies FormField<Record<string, any>>;
    case U21FormFieldTypes.TEXTAREA:
      return {
        ...baseField,
        type: U21FormFieldTypes.TEXTAREA,
      } satisfies FormField<Record<string, any>>;
    default:
      return { ...baseField, type: U21FormFieldTypes.TEXT } satisfies FormField<
        Record<string, any>
      >;
  }
};

const toGeneratedSection = (
  field: FormFieldArraySchema<any>,
  referenceTables: GoamlReferenceTables,
): FormFieldSection<Record<string, any>> => ({
  type: 'section',
  name: field.name,
  label: field.label ?? field.name,
  isHidden: field.hidden,
  repeatable: true,
  repeatableButtonLabel: field.label,
  defaultNumOfEntries: 0,
  mustExistInPayload: false,
  mustHaveValueIfExist: false,
  fields: adaptLegacySchema(field.fields, referenceTables),
});

const adaptCustomField = (
  field: FormFieldCustomSchema<any>,
  referenceTables: GoamlReferenceTables,
): FormFields<Record<string, any>> => {
  const nestedFields = field.fieldProps?.fields;
  if (!Array.isArray(nestedFields)) {
    return [];
  }
  const parentPath = field.name.split('.').slice(0, -1).join('.');

  return nestedFields.map((nestedField) =>
    toGeneratedField(
      {
        ...nestedField,
        name: parentPath
          ? `${parentPath}.${nestedField.name}`
          : nestedField.name,
        required: false,
      },
      referenceTables,
    ),
  );
};

export const adaptLegacySchema = (
  schema: FormSchema,
  referenceTables: GoamlReferenceTables,
): FormFields<Record<string, any>> =>
  schema.flatMap((field: LegacyField) => {
    if (field.type === 'ARRAY') {
      return [toGeneratedSection(field, referenceTables)];
    }

    if (field.type === 'CUSTOM') {
      return adaptCustomField(field, referenceTables);
    }

    return [toGeneratedField(field, referenceTables)];
  });
